/**
 * localStoragePreserver
 *
 * Utilities for clearing localStorage while preserving specific keys.
 *
 * Use case:
 * - We generally want a clean client state on logout (mappings, queues, UI prefs, etc.)
 * - But we still want to keep per-account "last active role" selections across logins
 *   in the same browser (supports multiple accounts in one browser).
 */

export type ClearLocalStoragePreservingOptions = {
  /**
   * Preserve any localStorage keys that start with one of these prefixes.
   * Example: ['activeRole_'] will preserve keys like `activeRole_123`.
   */
  preserveKeyPrefixes: string[];
};

export function clearLocalStoragePreserving(
  options: ClearLocalStoragePreservingOptions
): void {
  if (typeof window === 'undefined') return;

  const prefixes = Array.isArray(options.preserveKeyPrefixes)
    ? options.preserveKeyPrefixes.filter(Boolean)
    : [];

  if (prefixes.length === 0) {
    // No prefixes => preserve nothing; keep behavior identical to clear().
    try {
      window.localStorage.clear();
    } catch {
      // Best-effort.
    }
    return;
  }

  try {
    const preserved: Array<{ key: string; value: string }> = [];

    // Snapshot keys first: some browsers can behave oddly if storage mutates
    // during enumeration.
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key) keys.push(key);
    }

    for (const key of keys) {
      if (!prefixes.some(prefix => key.startsWith(prefix))) continue;
      const value = window.localStorage.getItem(key);
      if (typeof value === 'string') {
        preserved.push({ key, value });
      }
    }

    window.localStorage.clear();

    for (const { key, value } of preserved) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // Best-effort: ignore per-key failures (quota, privacy mode, etc.)
      }
    }
  } catch {
    // If anything goes wrong, fall back to best-effort clear so logout remains safe.
    try {
      window.localStorage.clear();
    } catch {
      // Ignore.
    }
  }
}

